import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  downloadTaxReconciliation,
  getTaxReconciliation,
  type TaxReconciliation,
  type TaxReconciliationParams,
  type TaxReconciliationPeriod,
} from "../../obras/obrasApi";
import { formatCurrency } from "../Obras/components/FinancialDashboardShared";
import {
  getIvaProvisionSuggestion,
  type IvaSuggestionGranularity,
} from "../../taxObligations/taxObligationsApi";
import {
  TaxObligationsPanel,
  type TaxObligationsPanelHandle,
} from "./TaxObligationsPanel";

type Granularity = "month" | "quarter";

// "Todos" os anos = não envia o parâmetro year (o backend trata null/ausente como todos).
const ALL_YEARS = "all" as const;

function buildYearOptions(): number[] {
  const current = new Date().getFullYear();
  return [current, current - 1, current - 2, current - 3];
}

// Saldo de IVA positivo = a entregar ao Estado; negativo = a recuperar.
function vatBalanceHint(balance: number): string {
  if (!Number.isFinite(balance) || balance === 0) return "—";
  return balance > 0 ? "a entregar ao Estado" : "a recuperar";
}

export function ConciliacaoFiscalView() {
  const yearOptions = useMemo(buildYearOptions, []);

  const [granularity, setGranularity] = useState<Granularity>("month");
  const [yearSelection, setYearSelection] = useState<string>(String(yearOptions[0]));
  const [includeUnvalidated, setIncludeUnvalidated] = useState(false);

  const [data, setData] = useState<TaxReconciliation | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  // Chave do período cuja sugestão de provisão de IVA está a carregar.
  const [provisionKey, setProvisionKey] = useState<string | null>(null);

  const obligationsPanelRef = useRef<TaxObligationsPanelHandle>(null);

  const currentParams = useCallback(
    (): TaxReconciliationParams => ({
      granularity,
      year: yearSelection === ALL_YEARS ? null : Number(yearSelection),
      includeUnvalidated,
    }),
    [granularity, yearSelection, includeUnvalidated],
  );

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      setData(await getTaxReconciliation(currentParams()));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Não foi possível carregar a conciliação fiscal.");
    } finally {
      setIsLoading(false);
    }
  }, [currentParams]);

  useEffect(() => {
    load();
  }, [load]);

  const handleExport = async (format: "excel" | "csv") => {
    if (isExporting) return;
    setIsExporting(true);
    setError(null);
    try {
      await downloadTaxReconciliation(currentParams(), format);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Não foi possível exportar a conciliação fiscal.");
    } finally {
      setIsExporting(false);
    }
  };

  // Sugestão de provisão de IVA para o período → pré-preenche o formulário de nova obrigação.
  const handleCreateProvision = async (period: TaxReconciliationPeriod) => {
    if (provisionKey) return;
    setProvisionKey(period.periodKey);
    setError(null);
    try {
      const suggestionGranularity: IvaSuggestionGranularity =
        granularity === "quarter" ? "quarterly" : "monthly";
      const suggestion = await getIvaProvisionSuggestion({
        year: period.year,
        granularity: suggestionGranularity,
        period: period.periodNumber,
      });
      obligationsPanelRef.current?.openCreate({
        kind: "iva",
        periodLabel: suggestion.periodLabel,
        amount: suggestion.suggestedAmount,
        dueDate: suggestion.suggestedDueDate,
      });
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Não foi possível calcular a provisão de IVA.",
      );
    } finally {
      setProvisionKey(null);
    }
  };

  const selectClass = "border border-border bg-background text-foreground text-sm px-3 py-2";
  const selectStyle = { borderRadius: "var(--radius-button)" } as const;
  const exportButtonClass =
    "inline-flex items-center justify-center px-4 py-2 min-h-[44px] border border-border bg-card text-foreground cursor-pointer transition-colors hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed text-sm";

  const periods = data?.periods ?? [];
  const totals = data?.totals;

  return (
    <div className="h-full overflow-auto bg-background">
      <div
        className="bg-card border-b border-border px-4 md:px-6"
        style={{
          paddingTop: "var(--spacing-page-header-top)",
          paddingBottom: "var(--spacing-page-header-bottom)",
        }}
      >
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <h1
              className="text-foreground"
              style={{
                fontSize: "var(--text-page-title)",
                fontWeight: "var(--font-weight-semi-bold)",
                lineHeight: "1.3",
              }}
            >
              Conciliação Fiscal
            </h1>
            <p
              style={{
                fontSize: "var(--text-base)",
                marginTop: "var(--spacing-title-subtitle)",
                color: "var(--subtitle-text)",
              }}
            >
              IVA suportado vs liquidado por período, para apoio à declaração fiscal
            </p>
          </div>
        </div>
      </div>

      <div className="px-4 md:px-6 py-6 md:py-8 space-y-6">
        {/* Controlos */}
        <div className="bg-card border border-border p-4 md:p-5" style={{ borderRadius: "var(--radius-card)" }}>
          <div className="flex flex-wrap items-end gap-4">
            <label className="flex flex-col gap-1 text-sm text-secondary-text">
              Granularidade
              <select
                value={granularity}
                onChange={(event) => setGranularity(event.target.value as Granularity)}
                className={selectClass}
                style={selectStyle}
              >
                <option value="month">Mensal</option>
                <option value="quarter">Trimestral</option>
              </select>
            </label>

            <label className="flex flex-col gap-1 text-sm text-secondary-text">
              Ano
              <select
                value={yearSelection}
                onChange={(event) => setYearSelection(event.target.value)}
                className={selectClass}
                style={selectStyle}
              >
                {yearOptions.map((year) => (
                  <option key={year} value={String(year)}>
                    {year}
                  </option>
                ))}
                <option value={ALL_YEARS}>Todos</option>
              </select>
            </label>

            <label className="flex items-center gap-2 text-sm text-foreground" style={{ minHeight: 44 }}>
              <input
                type="checkbox"
                checked={includeUnvalidated}
                onChange={(event) => setIncludeUnvalidated(event.target.checked)}
              />
              Incluir não validadas
            </label>

            <div className="flex flex-wrap gap-3" style={{ marginLeft: "auto" }}>
              <button
                type="button"
                onClick={() => handleExport("excel")}
                disabled={isExporting || isLoading}
                className={exportButtonClass}
                style={selectStyle}
              >
                {isExporting ? "A exportar…" : "Exportar Excel"}
              </button>
              <button
                type="button"
                onClick={() => handleExport("csv")}
                disabled={isExporting || isLoading}
                className={exportButtonClass}
                style={selectStyle}
              >
                {isExporting ? "A exportar…" : "Exportar CSV"}
              </button>
            </div>
          </div>
          <p className="text-secondary-text text-xs mt-3">
            Por defeito só são contabilizadas faturas validadas. Ative "Incluir não validadas" para considerar
            também as faturas ainda por validar.
          </p>
        </div>

        {error && (
          <div
            className="border border-destructive/30 bg-destructive/10 text-destructive px-4 py-3"
            style={{ borderRadius: "var(--radius)" }}
          >
            {error}
          </div>
        )}

        {/* Tabela */}
        <div className="bg-card border border-border overflow-x-auto" style={{ borderRadius: "var(--radius-card)" }}>
          <table className="w-full" style={{ minWidth: 900 }}>
            <thead>
              <tr className="border-b border-border bg-muted text-left">
                <th className="px-3 py-3">Período</th>
                <th className="px-3 py-3 text-right">Base Receitas</th>
                <th className="px-3 py-3 text-right">IVA Liquidado</th>
                <th className="px-3 py-3 text-right">Base Despesas</th>
                <th className="px-3 py-3 text-right">IVA Suportado</th>
                <th className="px-3 py-3 text-right">Saldo IVA</th>
                <th className="px-3 py-3 text-right">Nº</th>
                <th className="px-3 py-3 text-right">Provisão</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={8} className="px-4 py-6 text-center text-secondary-text">
                    A carregar…
                  </td>
                </tr>
              ) : periods.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-6 text-center text-secondary-text">
                    Sem faturas no período.
                  </td>
                </tr>
              ) : (
                periods.map((period) => (
                  <tr key={period.periodKey} className="border-b border-border last:border-b-0">
                    <td className="px-3 py-3 break-words text-foreground">{period.periodLabel}</td>
                    <td className="px-3 py-3 text-right whitespace-nowrap">{formatCurrency(period.revenueBase)}</td>
                    <td className="px-3 py-3 text-right whitespace-nowrap">{formatCurrency(period.vatCollected)}</td>
                    <td className="px-3 py-3 text-right whitespace-nowrap">{formatCurrency(period.expenseBase)}</td>
                    <td className="px-3 py-3 text-right whitespace-nowrap">{formatCurrency(period.vatPaid)}</td>
                    <td className="px-3 py-3 text-right whitespace-nowrap">
                      <span className={period.vatBalance >= 0 ? "text-foreground font-medium" : "text-green-700 font-medium"}>
                        {formatCurrency(period.vatBalance)}
                      </span>
                      <span className="block text-xs text-secondary-text">{vatBalanceHint(period.vatBalance)}</span>
                    </td>
                    <td className="px-3 py-3 text-right whitespace-nowrap text-secondary-text">
                      {period.revenueCount + period.expenseCount}
                    </td>
                    <td className="px-3 py-3 text-right whitespace-nowrap">
                      {period.vatBalance > 0 ? (
                        <button
                          type="button"
                          onClick={() => handleCreateProvision(period)}
                          disabled={provisionKey !== null}
                          className="inline-flex items-center justify-center px-3 py-1.5 min-h-[44px] border border-border bg-card text-foreground cursor-pointer transition-colors hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                          style={{ borderRadius: "var(--radius-button)" }}
                        >
                          {provisionKey === period.periodKey ? "A calcular…" : "Criar provisão de IVA"}
                        </button>
                      ) : (
                        <span className="text-secondary-text text-xs">—</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
            {totals && periods.length > 0 && (
              <tfoot>
                <tr className="border-t border-border bg-muted font-medium">
                  <td className="px-3 py-3 text-foreground">Total</td>
                  <td className="px-3 py-3 text-right whitespace-nowrap">{formatCurrency(totals.revenueBase)}</td>
                  <td className="px-3 py-3 text-right whitespace-nowrap">{formatCurrency(totals.vatCollected)}</td>
                  <td className="px-3 py-3 text-right whitespace-nowrap">{formatCurrency(totals.expenseBase)}</td>
                  <td className="px-3 py-3 text-right whitespace-nowrap">{formatCurrency(totals.vatPaid)}</td>
                  <td className="px-3 py-3 text-right whitespace-nowrap">
                    <span className={totals.vatBalance >= 0 ? "text-foreground" : "text-green-700"}>
                      {formatCurrency(totals.vatBalance)}
                    </span>
                    <span className="block text-xs text-secondary-text">{vatBalanceHint(totals.vatBalance)}</span>
                  </td>
                  <td className="px-3 py-3 text-right whitespace-nowrap text-secondary-text">
                    {totals.revenueCount + totals.expenseCount}
                  </td>
                  <td className="px-3 py-3" />
                </tr>
              </tfoot>
            )}
          </table>
        </div>

        {/* Obrigações fiscais — provisão de IVA sugerida + IRC/SS/outros por registo manual */}
        <TaxObligationsPanel ref={obligationsPanelRef} />
      </div>
    </div>
  );
}
